import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import z from 'zod'

import GradientWrapper from '@/common/GradientWrapper'
import { bridgelessChainInfo } from '@/config'
import { useKeplrWallet } from '@/contexts/KeplrWalletProvider'
import { formatBridgeAtomic } from '@/helpers'
import { parseHumanToAtomic } from '@/helpers/amount'
import { cn } from '@/theme/utils'
import { UiSkeleton } from '@/ui/UiSkeleton'

import { BecomeValidatorDetailsStep } from './BecomeValidatorDetailsStep'
import { useBecomeValidatorAmountPayload } from './hooks/use-become-validator-amount-payload'

type Step = 1 | 2

export function BecomeValidatorAmount({
  onClose,
  onSuccess,
  renderTitle,
  renderFooter,
}: {
  onClose: () => void
  onSuccess?: () => void
  renderTitle: (step: Step) => React.ReactNode
  renderFooter: (
    step: Step,
    opts: {
      canProceed: boolean
      isSubmitting: boolean
      onNext: () => void
      onBack?: () => void
    },
  ) => React.ReactNode
}) {
  const { address } = useKeplrWallet()
  const [step, setStep] = useState<Step>(1)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [amountHuman, setAmountHuman] = useState('')
  const [amountAtomic, setAmountAtomic] = useState<string | undefined>(
    undefined,
  )

  useEffect(() => {
    setStep(1)
    setIsSubmitting(false)
  }, [])

  useEffect(() => {
    if (step === 1) setIsSubmitting(false)
  }, [step])

  const {
    minimalDelegationAmount,
    walletBondAtomic,
    remainingAtomic,
    hasEnoughDelegation,
    remainingColorClass,
    isLoadingWalletBalances,
  } = useBecomeValidatorAmountPayload(address, amountAtomic)

  useEffect(() => {
    if (!amountHuman) {
      setAmountAtomic(undefined)
      return
    }

    try {
      const atomic = parseHumanToAtomic(
        amountHuman,
        bridgelessChainInfo.stakeCurrency.coinDecimals,
      )
      setAmountAtomic(atomic)
    } catch {
      setAmountAtomic(undefined)
    }
  }, [amountHuman])

  const canProceedToDetails = hasEnoughDelegation && !isLoadingWalletBalances

  return (
    <>
      {renderTitle(step)}

      <div className='text-foreground mt-4 flex flex-col gap-4 p-1 pr-4'>
        {step === 1 ? (
          <EnterAmountStep
            amountHuman={amountHuman}
            onAmountChange={setAmountHuman}
            isLoadingWalletBalances={isLoadingWalletBalances}
            minimalDelegationAmount={minimalDelegationAmount}
            walletBondAtomic={walletBondAtomic}
            remainingAtomic={remainingAtomic}
            hasEnoughDelegation={hasEnoughDelegation}
            remainingColorClass={remainingColorClass}
          />
        ) : (
          <BecomeValidatorDetailsStep
            onSuccess={onSuccess ?? onClose}
            onSubmittingChange={setIsSubmitting}
            amountAtomic={amountAtomic}
          />
        )}
      </div>

      {renderFooter(step, {
        canProceed: canProceedToDetails,
        isSubmitting,
        onNext: () => setStep(2),
        onBack: () => setStep(1),
      })}
    </>
  )
}

const amountSchema = z.object({
  amount: z.string().refine(
    val => {
      const trimmed = String(val ?? '').trim()
      if (!trimmed) return false
      try {
        parseHumanToAtomic(
          trimmed,
          bridgelessChainInfo.stakeCurrency.coinDecimals,
        )
        return true
      } catch {
        return false
      }
    },
    { message: 'Invalid amount format' },
  ),
})

type AmountFormValues = z.infer<typeof amountSchema>

function EnterAmountStep({
  amountHuman,
  onAmountChange,
  isLoadingWalletBalances,
  minimalDelegationAmount,
  walletBondAtomic,
  remainingAtomic,
  hasEnoughDelegation,
  remainingColorClass,
}: {
  amountHuman: string
  onAmountChange: (v: string) => void
  isLoadingWalletBalances: boolean
  minimalDelegationAmount: string
  walletBondAtomic: string
  remainingAtomic: string
  hasEnoughDelegation: boolean
  remainingColorClass: string
}) {
  const form = useForm<AmountFormValues>({
    resolver: zodResolver(amountSchema),
    mode: 'onChange',
    defaultValues: { amount: amountHuman },
  })

  const {
    register,
    setValue,
    formState: { errors },
  } = form

  const handleSetMax = () => {
    try {
      const maxHuman = formatBridgeAtomic(walletBondAtomic)
      const cleanMax = maxHuman.replace(/,/g, '')
      setValue('amount', cleanMax, { shouldValidate: true })
      onAmountChange(cleanMax)
    } catch {
      // ignore
    }
  }

  const handleChange = (value: string) => {
    setValue('amount', value)
    onAmountChange(value)
  }

  const balanceExceeded = amountHuman
    ? (() => {
        try {
          const inputAtomic = parseHumanToAtomic(
            amountHuman,
            bridgelessChainInfo.stakeCurrency.coinDecimals,
          )
          return BigInt(inputAtomic) > BigInt(walletBondAtomic)
        } catch {
          return false
        }
      })()
    : false

  return (
    <div className='space-y-3'>
      <div className='flex flex-col gap-1'>
        <span className='text-muted-foreground text-xs'>Amount (BRIDGE)</span>
        <GradientWrapper>
          <div className='relative'>
            <input
              {...register('amount', {
                onChange: e => handleChange(e.target.value),
              })}
              disabled={isLoadingWalletBalances}
              maxLength={30}
              className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 pr-16 text-sm outline-none disabled:opacity-60'
              placeholder='0.1'
            />
            <button
              type='button'
              onClick={handleSetMax}
              disabled={isLoadingWalletBalances}
              className='text-primary hover:text-primary/80 disabled:text-muted-foreground absolute top-1/2 right-3 -translate-y-1/2 text-xs font-medium transition-colors disabled:cursor-not-allowed'
            >
              Max
            </button>
          </div>
        </GradientWrapper>
        {errors.amount && (
          <span className='text-xs text-red-400'>
            {errors.amount.message as string}
          </span>
        )}
        {balanceExceeded && (
          <span className='text-xs text-red-400'>
            Amount exceeds wallet balance
          </span>
        )}
      </div>

      <div className='space-y-2 pt-1'>
        <div className='space-y-2'>
          <div className='bg-primary/10 flex items-center justify-between rounded-md px-3 py-2'>
            <span className='text-muted-foreground text-xs'>Required</span>
            <span className='font-semibold'>
              {isLoadingWalletBalances ? (
                <UiSkeleton className='h-4 w-24' />
              ) : (
                `${formatBridgeAtomic(minimalDelegationAmount)} BRIDGE`
              )}
            </span>
          </div>
          <div className='bg-primary/10 flex items-center justify-between rounded-md px-3 py-2'>
            <span className='text-muted-foreground text-xs'>
              Wallet Balance
            </span>
            <span className='font-semibold'>
              {isLoadingWalletBalances ? (
                <UiSkeleton className='h-4 w-24' />
              ) : (
                `${formatBridgeAtomic(walletBondAtomic)} BRIDGE`
              )}
            </span>
          </div>
          <div className='bg-primary/10 flex items-center justify-between rounded-md px-3 py-2'>
            <span className='text-muted-foreground text-xs'>Remaining</span>
            <span className={cn('font-semibold', remainingColorClass)}>
              {isLoadingWalletBalances ? (
                <UiSkeleton className='h-4 w-24' />
              ) : (
                `${formatBridgeAtomic(remainingAtomic, 18)} BRIDGE`
              )}
            </span>
          </div>
        </div>

        {!hasEnoughDelegation && !isLoadingWalletBalances && (
          <div className='text-muted-foreground text-xs'>
            Enter a higher amount to reach the required delegation.
          </div>
        )}

        {hasEnoughDelegation && (
          <div className='text-xs font-medium text-[#16D1D6]'>
            You entered enough to become a validator
          </div>
        )}
      </div>
    </div>
  )
}
